import {
  ensureProviderExists,
  handleError,
  mapFoldersToCourses,
  processResources,
} from '@/shared/utils/storeHelpers';
import { groupChatModelsByProvider } from '@/shared/utils/groupChatModelsByProvider';
import AwsAiService from '@/domains/@bot/chat/services/AwsAiService';
import PragyaAiService from '@/domains/@bot/chat/services/PragyaAiService';
import ValidateResponseService from '@/domains/@platform/admin/services/ValidateResponseService';
import LLMConfigService from '@/domains/@platform/admin/services/LLMConfigService';
import VertexAiService from '@/domains/@bot/chat/services/VertexAiService';

const providerHandlers = {
  aws: async (model, userPrompt, selected, sessionId) => {
    const req = {
      model,
      prompt: userPrompt,
      action: selected.action,
      course: selected.course,
      adminPrompt: selected.systemInstructions,
    };
    if (sessionId) req.sessionId = sessionId;

    const response = await AwsAiService.getAiResponse(req);
    const data = response.data?.body;

    let message;
    if (response.data.error) {
      throw new Error(response.data.error);
    }
    if (response.data.statusCode !== 200) {
      message = data.generated_text;
    } else if (selected.action === 'retrieve') {
      message = data.texts.flat().join('\n\n');
    } else if (selected.action === 'retrieve_and_generate') {
      message = data.generated_text;
    } else {
      message = 'Error in generating response.';
    }

    const processedResources = processResources(data.resources || [], 'aws');
    return {
      model,
      message,
      sessionId: data.session_id,
      resources: processedResources,
    };
  },

  pragya: async (model, userPrompt, selected, sessionId) => {
    const req = {
      model,
      question: userPrompt,
      course: selected.course,
    };
    if (sessionId) req.sessionId = sessionId;
    const response = await PragyaAiService.getAiResponse(req);
    if (response.data.error) {
      throw new Error(response.data.error);
    }

    return {
      model,
      message: response.data.answer,
      resources: [],
    };
  },

  vertex: async (model, userPrompt, selected, sessionId) => {
    const filters = Object.assign(
      {},
      ...selected.filters.map((f) => ({ [f.name]: f.value })),
    );

    const req = {
      question: userPrompt,
      model,
      systemInstruction: selected.systemInstructions,
      ...filters,
    };
    if (sessionId) req.sessionId = sessionId;

    const response = await VertexAiService.getAiResponse(req);

    const processedResources = processResources(
      response.data.references || [],
      'vertex',
    );

    return {
      model: model.name || model.label,
      message: response.data?.answer || response.data?.message || '',
      sessionId: response.data?.sessionId,
      resources: processedResources || [],
    };
  },
};

export default {
  namespaced: true,

  state: () => ({
    models: {},

    chat: {
      isGeneratingResponse: false,
      isFetchingOptions: false,
      messages: [],
      sessionIds: {},
      responseProvider: localStorage.getItem('responseProvider') || 'aws',
      error: null,
    },

    providers: {
      aws: {
        options: {
          actions: [
            { value: 'retrieve_and_generate', name: 'Retrieve and Generate' },
            { value: 'retrieve', name: 'Retrieve' },
          ],
          models: [
            { id: 'anthropic.claude-3-haiku-20240307-v1:0', name: 'Claude 3 Haiku', parent: 'Anthropic', provider: 'aws' },
            { id: 'anthropic.claude-3-sonnet-20240229-v1:0', name: 'Claude 3 Sonnet', parent: 'Anthropic', provider: 'aws' },
            { id: 'meta.llama3-8b-instruct-v1:0', name: 'Llama 3 8B Instruct', parent: 'Meta', provider: 'aws' },
            { id: 'meta.llama3-70b-instruct-v1:0', name: 'Llama 3 70B Instruct', parent: 'Meta', provider: 'aws' },
          ],
          courses: [],
        },
        selected: {
          action: 'retrieve_and_generate',
          models: [],
          course: null,
          systemInstructions: '',
        },
      },
      pragya: {
        options: {
          models: [],
          modes: [
            { value: 's3', name: 'S3' },
            { value: 'courseSection', name: 'Course Section' },
          ],
          courses: [],
          courseSection: [],
        },
        selected: {
          models: [],
          course: null,
          mode: { value: 's3', name: 'S3' },
        },
      },
      vertex: {
        options: {
          metadata: {},
          models: [],
        },
        selected: {
          filters: [],
          models: [],
          systemInstructions: '',
        },
      },
    },

    validateResponse: localStorage.getItem('validateResponse') || false,

    /** Per-feature effective models from GET llm-config/effective (institution + platform defaults). */
    featureEffectiveModels: {},
  }),

  getters: {
    currentProvider: (state) => state.providers[state.chat.responseProvider],
    allModels: (state) => state.models,
    modelsByProvider: (state) => (provider) => {
      return state.providers[provider]?.options?.models || [];
    },
    hasActiveSessions: (state) => Object.keys(state.chat.sessionIds)?.length > 0,
    lastUserMessage: (state) => {
      const messages = state.chat.messages;
      return messages?.length > 0 && messages[messages?.length - 1].role === 'user'
        ? messages[messages?.length - 1]
        : null;
    },
  },

  mutations: {
    setModels(state, { models = {}, reset = false }) {
      if (reset) {
        state.models = { ...models };
      } else {
        Object.keys(models).forEach((provider) => {
          if (!state.models[provider]) {
            state.models[provider] = [];
          }
          const existingIds = new Set(state.models[provider].map((model) => model.id));
          const newModels = models[provider].filter((model) => !existingIds.has(model.id));
          state.models[provider] = [...state.models[provider], ...newModels];
        });
      }
    },

    setProviderOptions(state, { provider, options, reset = false }) {
      ensureProviderExists(state, provider);
      const newOptions = reset
        ? { ...options }
        : { ...state.providers[provider].options, ...options };
      state.providers[provider].options = newOptions;
    },

    setProviderSelected(state, { provider, selected, reset = false }) {
      ensureProviderExists(state, provider);
      const newSelected = reset
        ? { ...selected }
        : { ...state.providers[provider].selected, ...selected };
      state.providers[provider].selected = newSelected;
    },

    addCourseToProvider(state, { provider, course }) {
      ensureProviderExists(state, provider);
      if (!state.providers[provider].options.courses) {
        state.providers[provider].options.courses = [];
      }
      const isPragyaProvider = provider === 'pragya';
      const coursesKey = isPragyaProvider ? 'courseSection' : 'courses';
      const existingCourse = state.providers[provider].options[coursesKey].find(
        (c) => c.value === course.value || c.name === course.name,
      );
      if (!existingCourse) {
        state.providers[provider].options[coursesKey].push(course);
      }
    },

    setResponseProvider(state, provider) {
      if (state.providers[provider]) {
        state.chat.responseProvider = provider;
        localStorage.setItem('responseProvider', provider);
      } else {
        console.warn(`Provider '${provider}' does not exist`);
      }
    },

    addMessage(state, { prompt, responses = [], role }) {
      if (role === 'user') {
        state.chat.messages.push({
          prompt,
          role,
          responses: [],
          timestamp: new Date().toISOString(),
        });
      } else if (role === 'ai') {
        const lastUserMessage = state.chat.messages[state.chat.messages?.length - 1];
        if (lastUserMessage && lastUserMessage.role === 'user') {
          lastUserMessage.responses = Array.isArray(responses) ? responses : [responses];
        } else {
          console.error('No matching user message found to attach AI responses.');
        }
      }
    },

    clearMessages(state) {
      state.chat.messages = [];
      state.chat.sessionIds = {};
    },

    setSessionId(state, { modelId, sessionId }) {
      state.chat.sessionIds[modelId] = sessionId;
    },

    clearSessions(state) {
      state.chat.sessionIds = {};
    },

    setIsGeneratingResponse(state, status) {
      state.chat.isGeneratingResponse = Boolean(status);
    },

    setIsFetchingOptions(state, status) {
      state.chat.isFetchingOptions = Boolean(status);
    },

    setError(state, error) {
      state.chat.error = error;
    },

    clearError(state) {
      state.chat.error = null;
    },

    setValidateResponse(state, flag) {
      state.validateResponse = flag;
      localStorage.setItem('validateResponse', JSON.stringify(flag));
    },

    setFeatureEffectiveModels(state, payload) {
      state.featureEffectiveModels = payload && typeof payload === 'object' ? { ...payload } : {};
    },

    RESET(state) {
      state.models = {};
      state.chat.isGeneratingResponse = false;
      state.chat.isFetchingOptions = false;
      state.chat.messages = [];
      state.chat.sessionIds = {};
      state.chat.error = null;
      state.validateResponse = false;
      state.featureEffectiveModels = {};
    },
  },

  actions: {
    setModels({ commit }, { models = [], reset = false }) {
      commit('setModels', { models, reset });
    },

    setProviderOptions({ commit }, { provider, options, reset = false }) {
      commit('setProviderOptions', { provider, options, reset });
    },

    setProviderSelected({ commit }, { provider, selected, reset = false }) {
      commit('setProviderSelected', { provider, selected, reset });
    },

    async addCourseToProvider({ commit, state }, { provider, course }) {
      try {
        if (!state.providers[provider]) {
          throw new Error(`Provider '${provider}' does not exist`);
        }
        if (!course) {
          throw new Error('Course is not valid');
        }
        const courseWithDefaults = { value: course, name: course };
        commit('addCourseToProvider', { provider, course: courseWithDefaults });
        return courseWithDefaults;
      } catch (error) {
        handleError(commit, error, 'addCourseToProvider');
        throw error;
      }
    },

    setResponseProvider({ commit }, provider) {
      commit('setResponseProvider', provider);
      commit('clearSessions');
      commit('setIsGeneratingResponse', false);
      commit('setIsFetchingOptions', false);
      commit('clearError');
    },

    addMessage({ commit }, { prompt, responses = [], role }) {
      commit('addMessage', { prompt, responses, role });
    },

    clearMessages({ commit }) {
      commit('clearMessages');
    },

    clearSessions({ commit }) {
      commit('clearSessions');
    },

    clearChat({ commit }) {
      commit('clearMessages');
      commit('clearSessions');
      commit('setIsGeneratingResponse', false);
      commit('setIsFetchingOptions', false);
      commit('clearError');
    },

    setIsFetchingOptions({ commit }, status) {
      commit('setIsFetchingOptions', status);
    },

    setError({ commit }, error) {
      commit('setError', error);
    },

    clearError({ commit }) {
      commit('clearError');
    },

    async fetchAwsOptions({ commit }) {
      commit('setIsFetchingOptions', true);
      commit('clearError');
      try {
        const response = await AwsAiService.getAllFolders();
        const courses = mapFoldersToCourses(response.data.body?.folders);
        commit('setProviderOptions', { provider: 'aws', options: { courses } });
      } catch (error) {
        handleError(commit, error, 'fetchAwsOptions');
      }
    },

    async fetchPragyaOptions({ commit, state, dispatch }) {
      commit('setIsFetchingOptions', true);
      commit('clearError');
      try {
        const coursesRes = await PragyaAiService.getAllCourses();
        const courseSection = mapFoldersToCourses(coursesRes.data?.folders);

        if (state.providers.aws.options.courses?.length === 0) {
          await dispatch('fetchAwsOptions');
        }

        const s3Courses = state.providers.aws.options.courses;
        const mode = state.providers.pragya.selected.mode.value || 's3';
        const courses = mode === 's3' ? s3Courses : courseSection;

        commit('setProviderOptions', {
          provider: 'pragya',
          options: { courses, courseSection },
        });
      } catch (error) {
        handleError(commit, error, 'fetchPragyaOptions');
      }
    },

    async fetchVertexOptions({ commit }) {
      commit('setIsFetchingOptions', true);
      commit('clearError');
      try {
        const response = await VertexAiService.getMetadataConfig();
        const data = response.data || {};
        const metadata = { ...data };
        delete metadata.models;
        commit('setProviderOptions', {
          provider: 'vertex',
          options: { metadata },
        });
      } catch (error) {
        handleError(commit, error, 'fetchVertexOptions');
      }
    },

    /**
     * Single source of truth: server llmModelCatalog (OpenAI / Anthropic / Google + optional Ollama).
     */
    async fetchEffectiveFeatureModels({ commit }) {
      try {
        const response = await LLMConfigService.getEffectiveModels();
        const { effectiveModels = {} } = response.data || {};
        commit('setFeatureEffectiveModels', effectiveModels);
      } catch (error) {
        console.error('fetchEffectiveFeatureModels:', error);
      }
    },

    async saveFeatureModel({ dispatch }, { featureKey, model }) {
      if (!featureKey || !model?.id || !model?.provider) {
        return;
      }
      try {
        await LLMConfigService.putOverride(featureKey, {
          model: {
            id: model.id,
            provider: model.provider,
            name: model.name || model.label || '',
          },
        });
        await dispatch('fetchEffectiveFeatureModels');
      } catch (error) {
        console.error('saveFeatureModel:', error);
        throw error;
      }
    },

    async syncChatModelCatalogFromServer({ commit, state }) {
      try {
        const response = await PragyaAiService.getChatModelCatalog();
        const { catalog, ollama = [] } = response.data || {};
        if (!catalog) {
          return;
        }
        const tag = (rows, provider, parent) =>
          (rows || []).map((m) => ({
            ...m,
            provider: m.provider || provider,
            parent: m.parent || parent,
          }));
        const pragyaModels = [
          ...tag(catalog.openai, 'openai', 'OpenAI'),
          ...tag(catalog.anthropic, 'anthropic', 'Anthropic'),
          ...tag(catalog.vertex, 'vertex', 'Google'),
          ...tag(ollama, 'ollama', 'Ollama'),
        ];
        commit('setProviderOptions', {
          provider: 'pragya',
          options: { models: pragyaModels },
        });

        const vertexOpts = state.providers.vertex?.options || {};
        commit('setProviderOptions', {
          provider: 'vertex',
          options: { ...vertexOpts, models: catalog.vertex || [] },
        });
      } catch (error) {
        console.error('syncChatModelCatalogFromServer:', error);
      }
    },

    async fetchValidateResponse({ commit }) {
      commit('setIsFetchingOptions', true);
      commit('clearError');
      try {
        const response = await ValidateResponseService.getValidateResponseFlag();
        const data = response.data || {};
        const { validateResponse } = data;
        commit('setValidateResponse', validateResponse);
      } catch (error) {
        handleError(commit, error, 'fetchValidateResponse');
      }
    },

    async generateResponse({ commit, state }, { prompt }) {
      commit('setIsGeneratingResponse', true);
      commit('clearError');
      try {
        const provider = state.chat.responseProvider;
        const providerConfig = state.providers[provider];
        const selectedModels = providerConfig.selected.models || [];

        if (selectedModels?.length === 0) {
          throw new Error(`No models selected for ${provider} provider`);
        }

        await this.dispatch('ai/addMessage', { prompt, role: 'user' });

        const handler = providerHandlers[provider];
        if (!handler) {
          throw new Error(`No handler found for provider: ${provider}`);
        }

        const responses = await Promise.all(
          selectedModels.map(async (model) => {
            const modelSessionId = state.chat.sessionIds[model.id] || null;
            let response;
            try {
              response = await handler(model, prompt, providerConfig.selected, modelSessionId);
              if (response.sessionId) {
                commit('setSessionId', { modelId: model.id, sessionId: response.sessionId });
              }
              return response;
            } catch (modelError) {
              console.error(`Error with model ${model.name}:`, modelError);
              return { ...response, error: true };
            }
          }),
        );

        const aiResponses = responses.map(({ model, message, resources, sessionId }) => ({
          model, message, resources, sessionId,
        }));
        await this.dispatch('ai/addMessage', { responses: aiResponses, role: 'ai' });
      } catch (error) {
        handleError(commit, error, 'generateResponse');
      } finally {
        commit('setIsGeneratingResponse', false);
      }
    },

    async resetProviderSessions({ commit, state }, provider) {
      const providerModels = state.providers[provider]?.selected?.models || [];
      providerModels.forEach((model) => {
        commit('setSessionId', { modelId: model.id, sessionId: null });
      });
      if (provider !== state.chat.responseProvider) {
        await this.dispatch('ai/clearChat');
      }
    },

    async initializeProviders({ commit, dispatch, state }) {
      try {
        await Promise.all([
          dispatch('fetchAwsOptions'),
          dispatch('fetchPragyaOptions'),
          dispatch('fetchVertexOptions'),
          dispatch('fetchValidateResponse'),
        ]);
        await dispatch('syncChatModelCatalogFromServer');
        await dispatch('fetchEffectiveFeatureModels');
        const flat = state.providers.pragya.options.models || [];
        const models = groupChatModelsByProvider(flat);
        commit('setModels', { models, reset: true });
      } catch (error) {
        console.error('Failed to initialize some providers:', error);
      } finally {
        commit('setIsFetchingOptions', false);
      }
    },
  },
};
